import { PropsWithChildren, createContext, useState, useCallback, useEffect } from 'react'
import AsyncStorage from '@react-native-async-storage/async-storage'
import { SecureConfig } from '@/modules/Login/hooks/types'
import { applySecureConfig } from '@/utils/configLoader'

interface ConfigContextProps {
  secureConfig: SecureConfig | null
  isLoading: boolean
  error: string | null
  storeConfig: (config: SecureConfig) => Promise<void>
  clearConfig: () => void
}

export const ConfigContext = createContext({} as ConfigContextProps)

const STORAGE_KEY = 'secure-config'

export const ConfigProvider = ({ children }: PropsWithChildren) => {
  const [secureConfig, setSecureConfig] = useState<SecureConfig | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Cargar configuración desde AsyncStorage al iniciar
  useEffect(() => {
    const loadConfigFromStorage = async () => {
      try {
        const cachedConfig = await AsyncStorage.getItem(STORAGE_KEY)
        if (cachedConfig) {
          const config = JSON.parse(cachedConfig) as SecureConfig
          setSecureConfig(config)
          applySecureConfig(config)
          console.log('Configuración cargada desde AsyncStorage')
        }
      } catch (err) {
        console.error('Error al cargar configuración desde AsyncStorage:', err)
      }
    }

    loadConfigFromStorage()
  }, [])

  const storeConfig = useCallback(async (config: SecureConfig) => {
    setIsLoading(true)
    setError(null)

    try {
      console.log('Almacenando configuración desde /me endpoint')

      if (!config.apiUrls?.app || !config.apiUrls?.monitor) {
        throw new Error('Configuración inválida: faltan URLs de API')
      }

      if (!config.azure?.storageAccountName) {
        throw new Error('Configuración inválida: falta nombre de cuenta Azure')
      }

      if (
        !config.azure?.containers?.necropsy?.sasToken ||
        !config.azure?.containers?.logistic?.sasToken
      ) {
        throw new Error('Configuración inválida: faltan tokens SAS de Azure')
      }

      setSecureConfig(config)
      await AsyncStorage.setItem(STORAGE_KEY, JSON.stringify(config))

      console.log('Configuración almacenada exitosamente desde /me')
      setError(null)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error desconocido'
      console.error('Error al almacenar configuración:', errorMessage)
      setError(errorMessage)
      throw err
    } finally {
      setIsLoading(false)
    }
  }, [])

  const clearConfig = useCallback(async () => {
    setSecureConfig(null)
    setError(null)
    await AsyncStorage.removeItem(STORAGE_KEY)
    console.log('Configuración segura limpiada')
  }, [])

  return (
    <ConfigContext.Provider
      value={{
        secureConfig,
        isLoading,
        error,
        storeConfig,
        clearConfig,
      }}
    >
      {children}
    </ConfigContext.Provider>
  )
}
